import { ActionTypes } from 'constants/MogamboConstants';
import uniq from 'lodash/uniq';

import { register } from '@src/dispatcher';
import createStore from './StoreUtils.js';

/**
 * User Says Store
 * All the store values that are related to user responses are stored here
 * @type {[type]}
 */
const UserSaysStore = createStore({
  /**
   * _allUserResponses: array of user responses for a particular node
   * _editedUserResponses: array of modified user responses that need to be saved
   * _meanings: array of meanings for a particular root word
   * _synonyms: array of synonym words supported for a node
   * _editedSynonyms: array of edited synonyms to keep track for saving later
   * @type {[type]}
   */
  _allUserResponses: undefined,
  _originalResponses: undefined,
  _userSaysMeta: {},
  _editedUserResponses: {},
  _meanings: {},
  _synonyms: {},
  _editedSynonyms: {},
  _entities: {},
  _editedEntities: {},
  _disambiguationTexts: {},
  /**
   * Reset back to the original empty store
   */
  reset() {
    this._allUserResponses = undefined;
    this._originalResponses = undefined;
    this._editedUserResponses = {};
    this._meanings = {};
    this._synonyms = {};
    this._editedSynonyms = {};
    this._disambiguationTexts = {};
  },

  /**
   * Reset all edited responses
   */
  resetEdited() {
    if (this._originalResponses) {
      this._editedUserResponses = {};
      this._editedSynonyms = {};
      this._editedEntities = {};
      this.setAllUserResponses(this._originalResponses, true);
    }
  },

  /**
   * Save all responses to the store
   * before saving, we sort the responses based on if they're active or not
   * and then based on the title
   * @param {[type]} data [description]
   */
  setAllUserResponses(data, isUpdate) {
    this._allUserResponses = data;
    const userResponses = data.user_responses;
    const synonyms = data.domain_synonyms || {};
    const entities = data.node_entities || {};

    if (!isUpdate) {
      this._originalResponses = JSON.parse(JSON.stringify(data));
    }

    Object.keys(userResponses).map((key) => {
      userResponses[key] = userResponses[key].sort((a, b) => {
        if (a.active === b.active) return 0;
        if (a.active) return -1;
        if (b.active) return 1;
        if (a.title === '') return 1;
        if (b.title === '') return -1;
        return 0;
      });
    });

    this._allUserResponses = userResponses;
    this._synonyms = synonyms;
    this._entities = entities;
    this._editedSynonyms = {};
    this._editedEntities = {};
  },

  /**
   * Saves all the disambiguation texts of edited languages
   * @param {Object} data [description]
   */
  setDisambiguationTexts(data, callback) {
    this._disambiguationTexts = data;
    if(callback) callback();
  },

  setUserSaysMeta(data) {
    this._userSaysMeta = data;
  },

  /**
   * Get User Responses
   * @return {[type]} [description]
   */
  getAllUserResponses() {
    return this._allUserResponses;
  },

  getDisambiguationTexts() {
    return this._disambiguationTexts;
  },

  /**
   * Get User Responses meta
   * @return {[type]} [description]
   */
  getUserSaysMeta() {
    return this._userSaysMeta;
  },

  /**
   * Get Edited User Responses
   * @return {[type]} [description]
   */
  getEditedUserResponses() {
    return this._editedUserResponses;
  },

  /**
   * Set Edited Responses
   * @param {[type]} data [description]
   */
  setEditedUserResponses(data) {
    this._editedUserResponses = data;
  },

  /**
   * Update user responses, also store the edited data in the edited user
   * responses key. It'll be used to save all the edited data
   * @param  {[type]} editedData    [description]
   * @param  {[type]} userResponses [description]
   * @return {[type]}               [description]
   */
  updateAllUserResponses(editedData, userResponses) {
    this._allUserResponses = userResponses;
    this._editedUserResponses = editedData;
    UserSaysStore.emitChange();
  },

  /**
   * Updater for updating user responses in case negative responses were added/removed
   * @param  {[type]} userResponses [description]
   * @return {[type]}               [description]
   */
  updateNegativeUserResponses(userResponses) {
    this._allUserResponses = userResponses;
    this._editedUserResponses.negative = userResponses.negative;
    UserSaysStore.emitChange();
  },

  /**
   * Get Synonyms
   * @return {[type]} [description]
   */
  getSynonyms() {
    return this._synonyms;
  },

  /**
   * Set Synonyms
   * We make sure that the synonyms stored are uniqe, so we deduplicate them
   * @param {[type]} synonyms [description]
   */
  setSynonyms(synonyms) {
    this._synonyms = Array.isArray(this._synonyms) ? this._synonyms : [];
    this._synonyms = uniq(this._synonyms.concat(synonyms), (synonym) =>
      synonym.source_word.toString()
    );
  },

  /**
   * Update the list of synonyms.
   * We also keep track of these changes in the _editedSynonyms
   * @param  {[type]} updatedCluster [description]
   * @return {[type]}                [description]
   */
  updateSynonyms(updatedCluster) {
    // sets edited Synonyms equal to synonyms to mock data,
    // only the updated Cluster is then updated based on it's key
    if (Object.keys(this._editedSynonyms).length !== 0) {
      const oldSynonyms = { ...this._editedSynonyms };
      this._synonyms = oldSynonyms;
    }

    const newSynonyms = { ...this._synonyms };
    newSynonyms[updatedCluster.cluster_name] = { ...updatedCluster };
    this._editedSynonyms = newSynonyms;
  },

  updateEntities(updatedEntity) {
    // sets edited Entities equal to entities to mock data,
    // only the updated entity is then updated based on it's key
    if (Object.keys(this._editedEntities).length !== 0) {
      const oldEntities = {
        ...this._editedEntities,
      };
      this._entities = oldEntities;
    }

    const newEntities = {
      ...this._entities,
      [updatedEntity.entity]: updatedEntity,
    };

    this._editedEntities = newEntities;
  },

  /**
   * Get a list of edited Synonyms
   * @return {[type]} [description]
   */
  getEditedSynonyms() {
    return this._editedSynonyms;
  },

  /**
   * Get Entities
   * @return {[type]} [description]
   */
  getEntities() {
    return this._entities;
  },

  /**
   * Update the list of entities.
   * We also keep track of these changes in the _editedEntities
   * @param  {[type]} updatedEntity [description]
   * @return {[type]}                [description]
   */

  /**
   * Get a list of edited Synonyms
   * @return {[type]} [description]
   */
  getEditedEntities() {
    return this._editedEntities;
  },

  /**
   * Get a list of meanings for a root word
   * @return {[type]} [description]
   */
  getMeanings() {
    return this._meanings;
  },

  /**
   * Set meaning for a root word
   * @param {[type]}   meanings [description]
   * @param {[type]}   message  [description]
   * @param {Function} callback [description]
   */
  setMeanings(meanings, message, callback) {
    this._meanings = meanings;
    if (callback) callback();
  },
});

/**
 * Listen to actions that are dispatched by the dispatcher and accordingly
 * call some store functions depending on the ActionType
 * @type {[type]}
 */
UserSaysStore.dispatchToken = register((action) => {
  const actionType = action.type;
  action = action.action;
  switch (actionType) {
    case ActionTypes.RECEIVE_USER_RESPONSES:
      UserSaysStore.setAllUserResponses(action.data);
      UserSaysStore.setUserSaysMeta(action.meta);
      UserSaysStore.setEditedUserResponses({});
      UserSaysStore.emitChange();
      if (action.callback) setTimeout(action.callback, 0);
      break;

    case ActionTypes.UPDATE_USER_RESPONSES:
      UserSaysStore.updateAllUserResponses(
        action.editedData,
        action.userResponses
      );
      break;

    case ActionTypes.RECEIVE_DISAMBIGUATION_TEXTS:
      UserSaysStore.setDisambiguationTexts(action.data, action.callback);
      break;

    case ActionTypes.UPDATE_NEGATIVE_USER_RESPONSES:
      UserSaysStore.updateNegativeUserResponses(action.userResponses);
      break;

    case ActionTypes.GET_SYNONYMS:
      break;

    case ActionTypes.SET_SYNONYMS:
      UserSaysStore.setSynonyms(action.data, action.message);
      UserSaysStore.emitChange();
      break;

    case ActionTypes.UPDATE_SYNONYMS:
      UserSaysStore.updateSynonyms(action.data);
      UserSaysStore.emitChange();
      break;

    case ActionTypes.UPDATE_ENTITIES:
      UserSaysStore.updateEntities(action.data);
      UserSaysStore.emitChange();
      break;

    case ActionTypes.GET_MEANINGS:
      break;

    case ActionTypes.SET_MEANINGS:
      UserSaysStore.setMeanings(action.data, action.message, action.callback);
      UserSaysStore.emitChange();
      break;

    case ActionTypes.SET_URL_METADATA:
      /**
       * Resetting the user says store is commented out because sometimes URL Meta calls finishes
       * after userResponses API call which leads to resetting of the data.
       */
      // UserSaysStore.reset();
      UserSaysStore.emitChange();
      break;

    case ActionTypes.RESET_EDITED:
      UserSaysStore.resetEdited();
      UserSaysStore.emitChange();
      break;

    case ActionTypes.UPDATE_CURRENT_LANGUAGE:
      UserSaysStore.reset();
      UserSaysStore.emitChange();
      break;

    default:
  }
});

export default UserSaysStore;
